import { DesignObject } from '../types/interfaces';

/** Order-independent snapshot (selection reorders objects for z-order; that isn't a change). */
export function canonicalDesign(objects: DesignObject[]): string {
  return JSON.stringify(
    [...(objects || [])]
      .map((o) => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined).sort(([a], [b]) => a.localeCompare(b))))
      .sort((a, b) => String(a.id).localeCompare(String(b.id)))
  );
}

export function isDesignDirty(objects: DesignObject[], baseline: string | null): boolean {
  if (baseline == null) return false;
  return canonicalDesign(objects) !== baseline;
}

export const draftKey = (siteId: string) => `tilly.facilityDesigner.draft.${siteId}`;

export interface DesignDraft {
  siteId: string;
  objects: DesignObject[];
  savedAt: string;
}

export function saveDraft(siteId: string, objects: DesignObject[], storage: Storage = localStorage) {
  try {
    storage.setItem(draftKey(siteId), JSON.stringify({ siteId, objects, savedAt: new Date().toISOString() }));
  } catch {
    /* storage full / private mode */
  }
}

export function loadDraft(siteId: string, storage: Storage = localStorage): DesignDraft | null {
  try {
    const raw = storage.getItem(draftKey(siteId));
    if (!raw) return null;
    const d = JSON.parse(raw);
    return d && Array.isArray(d.objects) ? d : null;
  } catch {
    return null;
  }
}

export function clearDraft(siteId: string, storage: Storage = localStorage) {
  try {
    storage.removeItem(draftKey(siteId));
  } catch {
    /* ignore */
  }
}

/**
 * Clicks that may navigate away from the designer: links anywhere, and any
 * button in the slide-out menu or bottom navbar (menu items navigate via JS),
 * except the hamburger itself.
 */
export function isLeavingClick(target: Element | null): Element | null {
  if (!target) return null;
  const el = target.closest('a[href], nav.menu button, nav.navbar-mobile button');
  if (!el) return null;
  if (el.closest('.hamburger')) return null;
  if (el.closest('[data-designer-root]')) return null; // our own UI
  if (el.tagName === 'A') {
    const a = el as HTMLAnchorElement;
    if (a.target === '_blank' || a.hasAttribute('download')) return null;
    if (a.getAttribute('href')?.startsWith('#')) return null;
  }
  return el;
}
